import type { StateService } from "@uirouter/angularjs";
import type { IComponentController, IScope } from "angular";
import { finalize } from "rxjs/operators";

import type { ICompany, IResponseWithLoading } from "App/Modules/Models";
import type { ICompaniesRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { PatchDelta } from "App/Modules/Services";

import { cloneDeep, filter, get, isEmpty, isEqual } from "lodash";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class EditCompanyComponentController implements IComponentController {
    public static $inject: string[] = ["$scope", "$state", "mmCompaniesRepository"];

    public resolve: IFormContainerResolve<ICompany, number>;

    public originalCompany: ICompany;

    public editedCompany: ICompany;

    public sitesCount: number;

    public companies: IResponseWithLoading<ICompany[]>;

    public companyToMergeWith: ICompany;

    public editResponse: IResponseWithLoading<ICompany>;

    public mergeResponse: IResponseWithLoading<void>;

    public deleteResponse: IResponseWithLoading<void>;

    constructor(
        private readonly $scope: IScope,
        private readonly $state: StateService,
        private readonly companiesRepository: ICompaniesRepository,
    ) {}

    public $onInit() {
        this.originalCompany = this.resolve.formData;
        this.sitesCount = this.resolve.bindings;
        this.editedCompany = cloneDeep(this.originalCompany);

        extendWithLoadingAndFail(this.companiesRepository.getAllCompanies(get(this.originalCompany.partner, "id")))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.companies = response;
                },
                (error) => {
                    this.companies = error;
                },
            );
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public filteredCompanies(): ICompany[] {
        return filter(this.companies.data, (company: ICompany) => company.id !== this.originalCompany.id);
    }

    public isCompanyEdited(): boolean {
        return !isEqual(this.originalCompany, this.editedCompany);
    }

    public editCompany() {
        const patchObject = PatchDelta.difference(this.originalCompany, this.editedCompany);

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(this.companiesRepository.patchCompany(this.editedCompany.id, patchObject))
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (editResponse) => {
                        this.editResponse = editResponse;
                        if (!editResponse.isLoading && editResponse.data) {
                            this.editedCompany = editResponse.data;
                            this.resolve.options.onOk(this.editedCompany);
                        }
                    },
                    (error) => {
                        this.editResponse = error;
                    },
                );
        } else if (!isEmpty(this.companyToMergeWith)) {
            extendWithLoadingAndFail(
                this.companiesRepository.mergeCompany(this.editedCompany.id, this.companyToMergeWith.id),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (mergeResponse) => {
                        this.mergeResponse = mergeResponse;
                        if (!mergeResponse.isLoading) {
                            this.cancel();
                            this.$state.go("companies.company", {
                                companyId: this.companyToMergeWith.id,
                            });
                        }
                    },
                    (error) => {
                        this.mergeResponse = error;
                    },
                );
        } else {
            this.cancel();
        }
    }

    public deleteCompany() {
        extendWithLoadingAndFail(this.companiesRepository.deleteCompany(this.editedCompany.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (deleteResponse) => {
                    this.deleteResponse = deleteResponse;

                    if (!deleteResponse.isLoading) {
                        this.resolve.options.onOk(null);
                        this.$state.go("companies");
                    }
                },
                (error) => {
                    this.deleteResponse = error;
                },
            );
    }
}
